/**
 * Storage-Gesundheitscheck (Dev + Production, ohne DevTools-Bundle).
 * Read-only by default — self-heal belongs in the repair package.
 */

import { getCurrentBusinessId } from '../../utils/sharedCore';
import {
  countEntityStoresByType,
  isEntityStoreAuthorityEnabled
} from './entityAggregateStore';
import {
  compareBlobWithEntityStores,
  isEntityStoreSkipBlobFieldsEnabled
} from './businessPayloadAssembly';
import { isEntityStoreNoKvBlobEnabled } from './businessPayloadMeta';
import { verifyEntityOnlyStorageHealth } from './entityOnlyStorageHealth';
import { auditEntityKvDrift } from './entityFieldSyncCheck';
import { runLocalStorageFullHealthReport } from '../../utils/localStorageHealth';
import { listKnownBusinessIds } from './businessIdDiscovery';
import { kvGet } from './kvAccess';

const CHECK_CACHE_TTL_MS = 4000;
/** @type {Map<string, { at: number, report: object, inflight?: Promise<object> }>} */
const checkCache = new Map();

/**
 * @param {string|null|undefined} businessId
 */
export function invalidateStorageCheckCache(businessId = null) {
  if (businessId) {
    checkCache.delete(String(businessId));
    return;
  }
  checkCache.clear();
}

/**
 * @param {string} [businessId]
 * @param {{ force?: boolean, includeAudit?: boolean, allowSelfHeal?: boolean }} [options]
 */
export async function runStorageCheck(businessId, options = {}) {
  let bid = businessId || getCurrentBusinessId({ allowFallback: false });
  if (!bid) {
    const known = await listKnownBusinessIds();
    bid = known?.[0] || null;
  }

  const cacheKey = bid || '__none__';
  const force = options.force === true;
  const cached = checkCache.get(cacheKey);

  if (!force && cached?.report && Date.now() - cached.at < CHECK_CACHE_TTL_MS) {
    return cached.report;
  }

  if (!force && cached?.inflight) {
    return cached.inflight;
  }

  const inflight = executeStorageCheck(bid, options).then((report) => {
    checkCache.set(cacheKey, { at: Date.now(), report });
    return report;
  }).catch((error) => {
    checkCache.delete(cacheKey);
    throw error;
  });

  checkCache.set(cacheKey, { at: Date.now(), report: cached?.report || null, inflight });
  return inflight;
}

/**
 * @param {string|null} bid
 * @param {{ includeAudit?: boolean, allowSelfHeal?: boolean }} options
 */
async function executeStorageCheck(bid, options = {}) {
  const report = {
    ok: true,
    businessId: bid,
    lines: [],
    details: {},
    checkedAt: new Date().toISOString()
  };

  if (!bid) {
    report.ok = false;
    report.lines.push('Keine Business-ID — bitte einloggen.');
    return report;
  }

  report.lines.push(`Business: ${bid}`);
  report.details.flags = {
    skipBlob: isEntityStoreSkipBlobFieldsEnabled(),
    noKvBlob: isEntityStoreNoKvBlobEnabled(),
    authority: isEntityStoreAuthorityEnabled()
  };
  report.lines.push(
    `Flags: SKIP_BLOB=${report.details.flags.skipBlob ? 'an' : 'aus'}, NO_KV_BLOB=${report.details.flags.noKvBlob ? 'an' : 'aus'}`
  );
  report.details.session = {
    currentUser: kvGet('currentUser'),
    currentBusiness: kvGet('currentBusiness')
  };

  // Menu/Settings health check stays read-only; repair package does mutations
  const health = await runLocalStorageFullHealthReport({
    includeAudit: options.includeAudit === true || process.env.NODE_ENV === 'development',
    allowSelfHeal: options.allowSelfHeal === true
  });
  report.details.health = health;
  report.lines.push(`KV Health: ${health?.ok ? 'OK' : 'Probleme'}`);
  if (!health?.ok) report.ok = false;

  const counts = await countEntityStoresByType(bid);
  report.details.entityCounts = counts;
  const entityTotal = Object.values(counts || {}).reduce((s, n) => s + Number(n || 0), 0);
  report.lines.push(`Entity-Einträge: ${entityTotal}`);

  const cmp = await compareBlobWithEntityStores(bid);
  report.details.compare = cmp;
  if (cmp?.allMatch) {
    report.lines.push('Blob vs entity_*: OK (allMatch)');
  } else if (entityTotal === 0 && (cmp?.entityTotal === 0 || !cmp?.blobKey)) {
    report.lines.push('Blob vs entity_*: leeres Business — normal nach Registrierung');
  } else {
    report.lines.push('Blob vs entity_*: UNTERSCHIEDE');
    report.ok = false;
  }

  const entityOnly = await verifyEntityOnlyStorageHealth(bid);
  report.details.entityOnly = entityOnly;
  if (entityOnly?.skipped) {
    report.lines.push(`Entity-only: übersprungen (${entityOnly.reason})`);
  } else {
    report.lines.push(`Entity-only: ${entityOnly?.ok ? 'OK' : 'nicht bereit'}`);
    if (!entityOnly?.ok) report.ok = false;
  }

  if (isEntityStoreNoKvBlobEnabled()) {
    const { readBestBusinessMergedPayload } = await import('../../utils/sharedCore');
    const kvPayload = readBestBusinessMergedPayload(bid).parsed || {};
    const drift = await auditEntityKvDrift(bid, kvPayload);
    report.details.kvEntityDrift = drift;
    if (drift.length > 0) {
      report.lines.push(
        `KV vs entity_* Drift: ${drift.length} Feld(er) — ${drift.map((d) => d.field).join(', ')}`
      );
      report.ok = false;
    } else {
      report.lines.push('KV vs entity_*: kein Drift');
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.info('[Storage Check]', report);
  }
  return report;
}
